import { Room } from 'livekit-client';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  CursorClickEvent,
  CursorMoveEvent,
  CursorPosition,
  VideoBounds,
  createCursorClickEvent,
  createCursorDragEndEvent,
  createCursorDragEvent,
  createCursorDragStartEvent,
  createCursorMoveEvent,
} from '../types/cursorEvents';

interface UseCursorSharingArgs {
  room: Room | null;
  isEnabled: boolean;
  shareId: string;
  videoBounds: VideoBounds | null;
}

interface UseCursorSharingReturn {
  publishCursorMove: (position: CursorPosition) => void;
  publishCursorClick: (position: CursorPosition) => void;
  publishCursorDragStart: (position: CursorPosition) => void;
  publishCursorDrag: (
    startPosition: CursorPosition,
    endPosition: CursorPosition,
  ) => void;
  publishCursorDragEnd: (
    startPosition: CursorPosition,
    endPosition: CursorPosition,
  ) => void;
  isConnected: boolean;
}

export function useCursorSharing({
  room,
  isEnabled,
  shareId,
  videoBounds,
}: UseCursorSharingArgs): UseCursorSharingReturn {
  const [isConnected, setIsConnected] = useState(false);
  const lastSentPosition = useRef<CursorPosition | null>(null);
  const lastSentTime = useRef<number>(0);
  const batchIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const pendingPositionRef = useRef<CursorPosition | null>(null);
  const isBatchRunningRef = useRef<boolean>(false);
  const stopBatchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Batch processing strategy:
  // 1. Store the latest cursor position in pendingPositionRef
  // 2. Start a 50ms interval timer if not already running
  // 3. Publish the latest position every 50ms while cursor is moving
  // 4. Stop the interval when no new positions are received

  // Check if we're connected to LiveKit
  useEffect(() => {
    if (!room) {
      console.log('🔌 No LiveKit room provided for cursor sharing');
      setIsConnected(false);
      return;
    }

    const handleConnected = () => {
      setIsConnected(true);
    };

    const handleDisconnected = () => {
      setIsConnected(false);
    };

    // Check initial connection state
    const initialConnected = room.state === 'connected';
    setIsConnected(initialConnected);

    // Listen for connection changes
    room.on('connected', handleConnected);
    room.on('disconnected', handleDisconnected);

    return () => {
      room.off('connected', handleConnected);
      room.off('disconnected', handleDisconnected);
    };
  }, [room, isEnabled, shareId]);

  // Batch processing cursor move publishing
  const publishCursorMove = useCallback(
    (position: CursorPosition) => {
      if (!isEnabled || !room?.localParticipant || !isConnected) {
        return;
      }

      // Store the latest position
      pendingPositionRef.current = position;

      // Clear any existing stop timeout
      if (stopBatchTimeoutRef.current) {
        clearTimeout(stopBatchTimeoutRef.current);
      }

      // Set timeout to stop batch processing if no new movements
      stopBatchTimeoutRef.current = setTimeout(() => {
        pendingPositionRef.current = null;
      }, 150); // Stop after 150ms of no new movements

      // Start batch processing if not already running
      if (!isBatchRunningRef.current) {
        isBatchRunningRef.current = true;

        const publishLatestPosition = () => {
          const positionToSend = pendingPositionRef.current;
          if (!positionToSend) {
            // No pending position, stop batch processing
            if (batchIntervalRef.current) {
              clearInterval(batchIntervalRef.current);
              batchIntervalRef.current = null;
            }
            isBatchRunningRef.current = false;
            return;
          }

          // Check if position has actually changed
          if (
            lastSentPosition.current &&
            lastSentPosition.current.x === positionToSend.x &&
            lastSentPosition.current.y === positionToSend.y
          ) {
            // Position hasn't changed, skip this update
            return;
          }

          const cursorMoveEvent: CursorMoveEvent = createCursorMoveEvent(
            positionToSend,
            videoBounds,
            shareId,
          );

          try {
            const eventData = JSON.stringify(cursorMoveEvent);
            room.localParticipant.publishData(
              new TextEncoder().encode(eventData),
              {
                topic: 'cursor-updates',
              },
            );
            lastSentPosition.current = positionToSend;
            lastSentTime.current = Date.now();
          } catch (error) {
            console.error('Failed to publish cursor move event:', error);
          }
        };

        // Publish immediately
        publishLatestPosition();

        // Then publish every 50ms
        batchIntervalRef.current = setInterval(publishLatestPosition, 50);
      }
    },
    [isEnabled, room, isConnected, videoBounds, shareId],
  );

  // Publish cursor click event
  const publishCursorClick = useCallback(
    (position: CursorPosition) => {
      if (!isEnabled || !room?.localParticipant || !isConnected) {
        return;
      }

      const cursorClickEvent: CursorClickEvent = createCursorClickEvent(
        position,
        videoBounds,
        shareId,
      );

      try {
        const eventData = JSON.stringify(cursorClickEvent);
        room.localParticipant.publishData(new TextEncoder().encode(eventData), {
          topic: 'cursor-updates',
        });
      } catch (error) {
        console.error('Failed to publish cursor click event:', error);
      }
    },
    [isEnabled, room, isConnected, videoBounds, shareId],
  );

  // Publish cursor drag start event
  const publishCursorDragStart = useCallback(
    (position: CursorPosition) => {
      if (!isEnabled || !room?.localParticipant || !isConnected) {
        return;
      }

      const cursorDragStartEvent = createCursorDragStartEvent(
        position,
        videoBounds,
        shareId,
      );

      try {
        const eventData = JSON.stringify(cursorDragStartEvent);
        room.localParticipant.publishData(new TextEncoder().encode(eventData), {
          topic: 'cursor-updates',
        });
      } catch (error) {
        console.error('Failed to publish cursor drag start event:', error);
      }
    },
    [isEnabled, room, isConnected, videoBounds, shareId],
  );

  // Publish cursor drag event
  const publishCursorDrag = useCallback(
    (startPosition: CursorPosition, endPosition: CursorPosition) => {
      if (!isEnabled || !room?.localParticipant || !isConnected) {
        return;
      }

      const cursorDragEvent = createCursorDragEvent(
        startPosition,
        endPosition,
        videoBounds,
        shareId,
      );

      try {
        const eventData = JSON.stringify(cursorDragEvent);
        room.localParticipant.publishData(new TextEncoder().encode(eventData), {
          topic: 'cursor-updates',
        });
      } catch (error) {
        console.error('Failed to publish cursor drag event:', error);
      }
    },
    [isEnabled, room, isConnected, videoBounds, shareId],
  );

  // Publish cursor drag end event
  const publishCursorDragEnd = useCallback(
    (startPosition: CursorPosition, endPosition: CursorPosition) => {
      if (!isEnabled || !room?.localParticipant || !isConnected) {
        return;
      }

      const cursorDragEndEvent = createCursorDragEndEvent(
        startPosition,
        endPosition,
        videoBounds,
        shareId,
      );

      try {
        const eventData = JSON.stringify(cursorDragEndEvent);
        room.localParticipant.publishData(new TextEncoder().encode(eventData), {
          topic: 'cursor-updates',
        });
      } catch (error) {
        console.error('Failed to publish cursor drag end event:', error);
      }
    },
    [isEnabled, room, isConnected, videoBounds, shareId],
  );

  // Note: Removed throttled publishing since debouncing handles the frequency control
  // The debounced approach in publishCursorMove is sufficient for smooth cursor tracking

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (batchIntervalRef.current) {
        clearInterval(batchIntervalRef.current);
      }
      if (stopBatchTimeoutRef.current) {
        clearTimeout(stopBatchTimeoutRef.current);
      }
    };
  }, []);

  return {
    publishCursorMove,
    publishCursorClick,
    publishCursorDragStart,
    publishCursorDrag,
    publishCursorDragEnd,
    isConnected,
  };
}
